import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import Chart from '../../components/ui/Chart';
import {
  BarChart3, TrendingUp, Activity, Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw, Download, Filter, Calendar, ArrowUp, ArrowDown, Settings
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const IntegrationAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showMetricModal, setShowMetricModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [filters, setFilters] = useState({
    timeRange: '7d',
    status: 'all',
    type: 'all'
  });
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [analyticsResponse, integrationsResponse] = await Promise.all([
          MockDataService.getIntegrationAnalytics(),
          MockDataService.getIntegrations()
        ]);

        if (analyticsResponse.success) {
          setAnalytics(analyticsResponse.data);
        }
        if (integrationsResponse.success) {
          setIntegrations(integrationsResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch integration analytics data.");
        console.error("Error fetching integration analytics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getIntegrationAnalytics();
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err) {
      console.error("Error refreshing analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await MockDataService.exportIntegrationAnalytics(filters);
      if (response.success) {
        const dataStr = JSON.stringify(analytics, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `integration-analytics-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        alert('Analytics data exported successfully!');
      }
    } catch (err) {
      alert('Failed to export analytics data');
      console.error("Error exporting analytics:", err);
    }
  };

  const handleConfigureIntegration = (integration) => {
    setSelectedIntegration(null);
    setShowDetailsModal(false);
    // Navigate to settings with the integration context
    alert(`Redirecting to configuration for "${integration.name}"...`);
  };

  const handleMetricClick = (metric) => {
    setSelectedMetric(metric);
    setShowMetricModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'error': return 'destructive';
      case 'warning': return 'outline';
      default: return 'outline';
    }
  };

  const getPerformanceColor = (value, threshold) => {
    if (value >= threshold) return 'text-green-600';
    if (value >= threshold * 0.8) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredIntegrations = integrations.filter(integration => {
    const matchesStatus = filters.status === 'all' || integration.status === filters.status;
    const matchesType = filters.type === 'all' || integration.type === filters.type;
    return matchesStatus && matchesType;
  });

  if (loading && !analytics) return <div className="p-6 text-center">Loading Integration Analytics...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="mb-6 bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Integration Analytics</h1>
            <p className="text-gray-600 mt-1">Monitor integration performance and health metrics.</p>
          </div>
          <div className="flex space-x-3">
            <Button 
              variant="outline" 
              onClick={handleRefresh} 
              disabled={loading}
              className="transition-all duration-300 hover:scale-105 px-6 py-3"
              style={{
                background: loading ? '#cccccc' : 'linear-gradient(135deg, #006600 0%, #BB0000 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportData}
              className="transition-all duration-300 hover:scale-105 px-6 py-3"
              style={{
                background: 'linear-gradient(135deg, #006600 0%, #BB0000 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-md border-2" style={{ borderColor: '#f0f0f0' }}>
        <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
          <CardTitle className="flex items-center text-gray-800">
            <Filter className="h-5 w-5 mr-2" style={{ color: '#307A91' }} />
            Filters & Time Range
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" style={{ color: '#307A91' }} />
              <select
                value={filters.timeRange}
                onChange={(e) => setFilters({...filters, timeRange: e.target.value})}
                className="px-4 py-3 border-2 rounded-md font-medium text-gray-800"
                style={{ borderColor: '#e0e0e0', minWidth: '160px' }}
              >
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-4 py-3 border-2 rounded-md font-medium text-gray-800"
              style={{ borderColor: '#e0e0e0', minWidth: '160px' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
            </select>

            <select
              value={filters.type}
              onChange={(e) => setFilters({...filters, type: e.target.value})}
              className="px-4 py-3 border-2 rounded-md font-medium text-gray-800"
              style={{ borderColor: '#e0e0e0', minWidth: '160px' }}
            >
              <option value="all">All Types</option>
              <option value="api">API Integration</option>
              <option value="webhook">Webhook</option>
              <option value="database">Database</option>
              <option value="email">Email Service</option>
              <option value="sms">SMS Service</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      {analytics && (() => {
        const metrics = [
          {
            title: 'Total Integrations',
            value: analytics.totalIntegrations,
            icon: Activity,
            bgColor: '#006600',
            textColor: '#FFFFFF',
            subtitle: `${analytics.activeIntegrations} active`,
            trendDirection: 'up',
            trend: '+2',
            details: [
              { label: 'Active Integrations', value: analytics.activeIntegrations },
              { label: 'Inactive Integrations', value: analytics.totalIntegrations - analytics.activeIntegrations },
              { label: 'API Integrations', value: Math.floor(analytics.totalIntegrations * 0.4) },
              { label: 'Webhook Integrations', value: Math.floor(analytics.totalIntegrations * 0.3) },
              { label: 'Database Connections', value: Math.floor(analytics.totalIntegrations * 0.2) },
              { label: 'Other Services', value: Math.floor(analytics.totalIntegrations * 0.1) }
            ]
          },
          {
            title: 'Success Rate',
            value: `${analytics.successRate}%`,
            icon: CheckCircle,
            bgColor: '#006600',
            textColor: '#FFFFFF',
            subtitle: analytics.successRate >= 95 ? 'Excellent' : analytics.successRate >= 85 ? 'Good' : 'Needs Attention',
            trendDirection: 'up',
            trend: '+2.3%',
            details: [
              { label: 'Overall Success Rate', value: `${analytics.successRate}%` },
              { label: 'Successful Requests (24h)', value: '12,458' },
              { label: 'Failed Requests (24h)', value: '342' },
              { label: 'Retry Success Rate', value: '87%' },
              { label: 'Peak Success Rate', value: '99.2%' },
              { label: 'Lowest Success Rate', value: '92.1%' }
            ]
          },
          {
            title: 'Avg Response Time',
            value: `${analytics.avgResponseTime}ms`,
            icon: Clock,
            bgColor: '#006600',
            textColor: '#FFFFFF',
            subtitle: analytics.avgResponseTime < 500 ? 'Fast' : analytics.avgResponseTime < 1000 ? 'Good' : 'Slow',
            trendDirection: 'down',
            trend: '-15ms',
            details: [
              { label: 'Average Response', value: `${analytics.avgResponseTime}ms` },
              { label: 'Fastest Response', value: '45ms' },
              { label: 'Slowest Response', value: '1,250ms' },
              { label: '95th Percentile', value: `${Math.floor(analytics.avgResponseTime * 1.5)}ms` },
              { label: '99th Percentile', value: `${Math.floor(analytics.avgResponseTime * 2)}ms` },
              { label: 'Median Response', value: `${Math.floor(analytics.avgResponseTime * 0.9)}ms` }
            ]
          },
          {
            title: 'Error Rate',
            value: `${analytics.errorRate}%`,
            icon: XCircle,
            bgColor: '#BB0000',
            textColor: '#FFFFFF',
            subtitle: analytics.errorRate < 2 ? 'Low' : analytics.errorRate < 5 ? 'Moderate' : 'High',
            trendDirection: 'down',
            trend: '-0.5%',
            details: [
              { label: 'Overall Error Rate', value: `${analytics.errorRate}%` },
              { label: 'Timeout Errors', value: '45%' },
              { label: 'Authentication Errors', value: '25%' },
              { label: 'Network Errors', value: '20%' },
              { label: 'Server Errors', value: '8%' },
              { label: 'Other Errors', value: '2%' }
            ]
          }
        ];

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              const TrendIcon = metric.trendDirection === 'up' ? ArrowUp : ArrowDown;
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 border-2"
                  onClick={() => handleMetricClick(metric)}
                  style={{ 
                    backgroundColor: metric.bgColor,
                    borderColor: metric.bgColor,
                    color: metric.textColor
                  }}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium" style={{ color: metric.textColor }}>{metric.title}</CardTitle>
                    <Icon className="h-5 w-5" style={{ color: metric.textColor }} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-2" style={{ color: metric.textColor }}>{metric.value}</div>
                    <p className="text-xs font-medium mb-2" style={{ color: metric.textColor, opacity: 0.9 }}>
                      {metric.subtitle}
                    </p>
                    <div 
                      className="flex items-center text-xs font-medium"
                      style={{ color: metric.textColor, opacity: 0.9 }}
                    >
                      <TrendIcon className="h-3 w-3 mr-1" />
                      {metric.trend} from last period
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        );
      })()}

      {/* Performance Trends */}
      {analytics && (() => {
        // Generate performance trend data
        const performanceTrendData = [
          { name: '00:00', requests: 450, errors: 8, avgTime: 285 },
          { name: '04:00', requests: 320, errors: 5, avgTime: 245 },
          { name: '08:00', requests: 890, errors: 15, avgTime: 312 },
          { name: '12:00', requests: 1250, errors: 28, avgTime: 295 },
          { name: '16:00', requests: 1420, errors: 32, avgTime: 268 },
          { name: '20:00', requests: 980, errors: 18, avgTime: 255 }
        ];

        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
                <CardTitle className="flex items-center text-gray-800">
                  <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#307A91' }} />
                  Performance Trends
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Chart Legend */}
                <div className="flex items-center justify-center space-x-6 mb-4 pb-4 border-b">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#006600' }}></div>
                    <span className="text-sm font-medium text-gray-700">Requests</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#BB0000' }}></div>
                    <span className="text-sm font-medium text-gray-700">Errors</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#FEC20C' }}></div>
                    <span className="text-sm font-medium text-gray-700">Avg Response (ms)</span>
                  </div>
                </div>

                <Chart
                  type="line"
                  data={performanceTrendData}
                  height={260}
                  colors={['#006600', '#BB0000', '#FEC20C']}
                  dataKeys={[
                    { dataKey: 'requests', name: 'Requests' },
                    { dataKey: 'errors', name: 'Errors' },
                    { dataKey: 'avgTime', name: 'Avg Response Time (ms)' }
                  ]}
                  options={{
                    legend: true
                  }}
                />
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-sm text-gray-600">Total Requests</div>
                    <div className="text-lg font-bold" style={{ color: '#006600' }}>
                      {performanceTrendData.reduce((sum, item) => sum + item.requests, 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Errors</div>
                    <div className="text-lg font-bold" style={{ color: '#BB0000' }}>
                      {performanceTrendData.reduce((sum, item) => sum + item.errors, 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Avg Response</div>
                    <div className="text-lg font-bold" style={{ color: '#006600' }}>
                      {Math.floor(performanceTrendData.reduce((sum, item) => sum + item.avgTime, 0) / performanceTrendData.length)}ms
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          <Card>
            <CardHeader>
              <CardTitle>Integration Health</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span>Overall Health Score:</span>
                <Badge variant={analytics.healthScore >= 90 ? 'success' : analytics.healthScore >= 70 ? 'secondary' : 'destructive'}>
                  {analytics.healthScore}/100
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span>Active Connections:</span>
                <span>{analytics.activeConnections}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Failed Connections:</span>
                <span className="text-red-600">{analytics.failedConnections}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Last Sync:</span>
                <span>{new Date(analytics.lastSync).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>
        );
      })()}

      {/* Integrations List */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Status ({filteredIntegrations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredIntegrations.map((integration) => (
              <div key={integration.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold">{integration.name}</h3>
                      <Badge variant={getStatusColor(integration.status)}>
                        {integration.status}
                      </Badge>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{integration.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Activity className="h-4 w-4 mr-1" />
                        {integration.type}
                      </span>
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {integration.lastActivity}
                      </span>
                      <span className="flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        {integration.successRate}% success
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => {
                        setSelectedIntegration(integration);
                        setShowDetailsModal(true);
                      }}
                      className="text-xs text-white"
                      style={{
                        background: 'linear-gradient(135deg, #006600 0%, #BB0000 100%)',
                        border: 'none'
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Integration Details Modal */}
      <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)}>
        <div className="max-w-3xl -m-6">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 rounded-t-lg relative">
            <button
              onClick={() => setShowDetailsModal(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              aria-label="Close"
            >
              <XCircle className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-3 pr-12">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Integration Details</h2>
                <p className="text-blue-100 text-sm mt-1">Comprehensive integration performance and configuration</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
            {selectedIntegration && (
              <>
                {/* Header Section */}
                <div className="flex justify-between items-start pb-4 border-b-2 border-gray-100">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{selectedIntegration.name}</h3>
                    <p className="text-gray-600 mt-1">{selectedIntegration.description}</p>
                  </div>
                  <Badge variant={getStatusColor(selectedIntegration.status)} className="text-sm px-3 py-1">
                    {selectedIntegration.status}
                  </Badge>
                </div>

                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-white rounded-lg border border-blue-100">
                    <div className="text-xs font-semibold text-blue-600 mb-1">TYPE</div>
                    <div className="text-sm font-medium text-gray-800">{selectedIntegration.type}</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-white rounded-lg border border-purple-100">
                    <div className="text-xs font-semibold text-purple-600 mb-1">VERSION</div>
                    <div className="text-sm font-medium text-gray-800">{selectedIntegration.version}</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-white rounded-lg border border-green-100">
                    <div className="text-xs font-semibold text-green-600 mb-1">SUCCESS RATE</div>
                    <div className="text-sm font-medium text-gray-800">{selectedIntegration.successRate}%</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-white rounded-lg border border-orange-100">
                    <div className="text-xs font-semibold text-orange-600 mb-1">AVG RESPONSE TIME</div>
                    <div className="text-sm font-medium text-gray-800">{selectedIntegration.avgResponseTime}ms</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-teal-50 to-white rounded-lg border border-teal-100">
                    <div className="text-xs font-semibold text-teal-600 mb-1">LAST ACTIVITY</div>
                    <div className="text-sm font-medium text-gray-800">{selectedIntegration.lastActivity}</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200">
                    <div className="text-xs font-semibold text-gray-600 mb-1">CREATED</div>
                    <div className="text-sm font-medium text-gray-800">{new Date(selectedIntegration.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Performance Metrics */}
                {selectedIntegration.metrics && (
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#307A91' }} />
                      Performance Metrics
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-xs font-medium text-blue-600 mb-1">Total Requests</div>
                        <div className="text-xl font-bold text-blue-700">{selectedIntegration.metrics.totalRequests.toLocaleString()}</div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-xs font-medium text-green-600 mb-1">Successful Requests</div>
                        <div className="text-xl font-bold text-green-700">{selectedIntegration.metrics.successfulRequests.toLocaleString()}</div>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="text-xs font-medium text-red-600 mb-1">Failed Requests</div>
                        <div className="text-xl font-bold text-red-700">{selectedIntegration.metrics.failedRequests.toLocaleString()}</div>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="text-xs font-medium text-purple-600 mb-1">Avg Response Time</div>
                        <div className="text-xl font-bold text-purple-700">{selectedIntegration.metrics.avgResponseTime}ms</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Errors */}
                {selectedIntegration.errors && selectedIntegration.errors.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                      Recent Errors
                    </h4>
                    <div className="space-y-2">
                      {selectedIntegration.errors.slice(0, 3).map((error, index) => (
                        <div key={index} className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                          <div className="text-sm font-semibold text-red-800">
                            {error.type}
                          </div>
                          <div className="text-sm text-red-700 mt-1">
                            {error.message}
                          </div>
                          <div className="text-xs text-red-600 mt-2">
                            {new Date(error.timestamp).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-8 py-5 bg-gray-50 rounded-b-lg border-t-2 border-gray-100 flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowDetailsModal(false)}
              className="px-6 py-3 border-2 hover:bg-gray-100 transition-all duration-200"
              style={{ borderColor: '#e0e0e0' }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Close
            </Button>
            <Button
              onClick={() => handleConfigureIntegration(selectedIntegration)}
              className="text-xs text-white"
              style={{
                background: 'linear-gradient(135deg, #006600 0%, #BB0000 100%)',
                border: 'none'
              }}
            >
              <Settings className="h-4 w-4 mr-2" />
              Configure Integration
            </Button>
          </div>
        </div>
      </Modal>

      {/* Metric Detail Modal */}
      {showMetricModal && selectedMetric && (
        <Modal
          isOpen={showMetricModal}
          onClose={() => setShowMetricModal(false)}
          title={selectedMetric.title}
          size="lg"
        >
          <div className="space-y-6 p-6">
            {/* Metric Header */}
            <div 
              className="p-6 rounded-lg text-white"
              style={{ 
                background: `linear-gradient(135deg, ${selectedMetric.bgColor} 0%, ${selectedMetric.bgColor === '#006600' ? '#BB0000' : '#006600'} 100%)` 
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium opacity-90 mb-1">Current Value</p>
                  <p className="text-4xl font-bold">{selectedMetric.value}</p>
                  <p className="text-sm font-medium mt-2 opacity-90">{selectedMetric.subtitle}</p>
                </div>
                <selectedMetric.icon className="h-16 w-16 opacity-80" />
              </div>
              <div className="mt-4 flex items-center text-sm font-medium">
                {selectedMetric.trendDirection === 'up' ? (
                  <ArrowUp className="h-4 w-4 mr-1" />
                ) : (
                  <ArrowDown className="h-4 w-4 mr-1" />
                )}
                <span>{selectedMetric.trend} from last period</span>
              </div>
            </div>

            {/* Metric Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              {selectedMetric.details.map((detail, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-gray-50 rounded-lg border-2 hover:shadow-md transition-all"
                  style={{ borderColor: '#f0f0f0' }}
                >
                  <p className="text-sm text-gray-600 mb-1">{detail.label}</p>
                  <p className="text-2xl font-bold" style={{ color: selectedMetric.bgColor }}>
                    {detail.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button 
                onClick={() => setShowMetricModal(false)}
                className="w-full h-full p-4 transition-all hover:shadow-lg hover:scale-[1.02] text-white"
                style={{
                  background: `linear-gradient(135deg, ${selectedMetric.bgColor} 0%, ${selectedMetric.bgColor === '#006600' ? '#BB0000' : '#006600'} 100%)`,
                  border: 'none'
                }}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setShowMetricModal(false);
                  handleRefresh();
                }}
                className="transition-all hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${selectedMetric.bgColor} 0%, ${selectedMetric.bgColor}dd 100%)`,
                  color: 'white',
                  border: 'none'
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Data
              </Button>
              <Button
                onClick={() => {
                  setShowMetricModal(false);
                  handleExportData();
                }}
                className="transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #BB0000%, #BB0000 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Data
              </Button>
              <Button
                onClick={() => {
                  setShowMetricModal(false);
                  alert('Opening integration settings...');
                }}
                className="transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #006600 0%, #006600 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default IntegrationAnalytics;

